import { useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  CheckFilterRow,
  PriceRangeSection,
  SingleChoiceSection,
} from '../../shared/results_page/results_filters_panel.js';
import {
  ResultsChipSheet,
  ResultsFilterChips,
  ResultsSortSheet,
  type ResultsFilterChip,
} from '../../shared/results_page/results_filter_chips.js';
import { activeFilterCount } from './hotel_results_list.js';
import {
  DISTANCE_THRESHOLDS,
  HOTEL_RATING_BAND_FLOORS,
  hotelRatingBandBounds,
  matchesHotelResultsFilters,
  useFilterCatalog,
  type HotelFilterableOffer,
  type HotelRatingBandFloor,
  type HotelResultsFiltersCopy,
  type HotelResultsFilterValue,
} from './hotel_results_filters.js';
import { HotelResultsFiltersSheet } from './hotel_results_filters_sheet.js';
import {
  DEFAULT_HOTEL_RESULTS_ORDER,
  HOTEL_RESULTS_ORDERS,
  type HotelResultsOrder,
  type HotelResultsSortCopy,
} from './hotel_results_sort.js';

/** A copy que só os chips precisam — o resto vem do painel e do ordenador. */
export type HotelResultsChipsCopy = {
  /** O nome acessível da fila ("Filtros e ordenação"). */
  toolbarLabel: string;
  /** O ← das folhas dos chips ("Voltar"). */
  backLabel: string;
  /** O rótulo do chip de diária com corte: só teto, só piso, os dois. */
  priceUpTo: (maximum: number) => string;
  priceFrom: (minimum: number) => string;
  priceBetween: (minimum: number, maximum: number) => string;
};

type OpenSheet = 'filters' | 'sort' | 'price' | 'distance' | 'rating' | null;

type HotelResultsFilterChipsProps = {
  readonly offers: readonly HotelFilterableOffer[];
  readonly currency: string;
  readonly filters: HotelResultsFilterValue;
  readonly order: HotelResultsOrder;
  readonly filtersCopy: HotelResultsFiltersCopy;
  readonly sortCopy: HotelResultsSortCopy;
  readonly copy: HotelResultsChipsCopy;
  readonly locked: boolean;
  readonly countsPartial: boolean;
  /** A fila recolhida enquanto a lista rola para baixo (#3274). */
  readonly collapsed?: boolean;
  readonly onQueryChange: (query: {
    filters: HotelResultsFilterValue;
    order: HotelResultsOrder;
  }) => void;
};

function priceChipLabel(
  filters: HotelResultsFilterValue,
  copy: HotelResultsChipsCopy,
  fallback: string,
): string {
  const { minimumNightlyPrice: min, maximumNightlyPrice: max } = filters;
  if (min !== undefined && max !== undefined) return copy.priceBetween(min, max);
  if (max !== undefined) return copy.priceUpTo(max);
  if (min !== undefined) return copy.priceFrom(min);
  return fallback;
}

function ratingBandLabel(floor: HotelRatingBandFloor, copy: HotelResultsFiltersCopy): string {
  const { from, to } = hotelRatingBandBounds(floor);
  return to === undefined
    ? copy.ratingThresholdLabel(from)
    : copy.ratingBandLabel(from, Math.round((to - 0.1) * 10) / 10);
}

type ChipsInput = Pick<
  HotelResultsFilterChipsProps,
  'offers' | 'filters' | 'order' | 'filtersCopy' | 'sortCopy' | 'copy'
> & {
  readonly hasPrice: boolean;
  readonly amenities: ReturnType<typeof useFilterCatalog>['amenities'];
  readonly applyFilters: (next: HotelResultsFilterValue) => void;
  readonly openSheet: (id: string, next: Exclude<OpenSheet, null>) => () => void;
};

/** Os chips do hotel na ordem do painel completo: fixos, liga/desliga, os de
 *  folha (diária, distância, nota) e um por comodidade ativa. */
function hotelChips(input: ChipsInput): ResultsFilterChip[] {
  const { offers, filters, order, filtersCopy, sortCopy, copy, hasPrice, amenities } = input;
  const { applyFilters, openSheet } = input;
  const toggle =
    (key: 'policyOnly' | 'breakfastOnly' | 'refundableOnly' | 'agreedRateOnly') => () => {
      applyFilters({ ...filters, [key]: filters[key] !== true });
    };
  const bands = filters.ratingBands ?? [];
  const count = activeFilterCount(filters);
  return [
    {
      id: 'filters',
      kind: 'filters',
      label: filtersCopy.filtersLabel,
      active: count > 0,
      badge: count,
      onPress: openSheet('filters', 'filters'),
    },
    {
      id: 'sort',
      kind: 'sort',
      label: sortCopy.orderLabel(order),
      active: order !== DEFAULT_HOTEL_RESULTS_ORDER,
      onPress: openSheet('sort', 'sort'),
    },
    {
      id: 'policy',
      kind: 'toggle',
      label: filtersCopy.withinPolicyLabel,
      active: filters.policyOnly,
      onPress: toggle('policyOnly'),
    },
    {
      id: 'breakfast',
      kind: 'toggle',
      label: filtersCopy.breakfastLabel,
      active: filters.breakfastOnly,
      onPress: toggle('breakfastOnly'),
    },
    {
      id: 'refundable',
      kind: 'toggle',
      label: filtersCopy.refundableLabel,
      active: filters.refundableOnly,
      onPress: toggle('refundableOnly'),
    },
    // #6928: só com hotel com acordo na lista (ou o corte já ligado) — cliente
    // sem acordo não vê o chip.
    ...(filters.agreedRateOnly === true || offers.some(({ agreedRate }) => agreedRate === true)
      ? [
          {
            id: 'agreed-rate',
            kind: 'toggle' as const,
            label: filtersCopy.agreedRateLabel,
            active: filters.agreedRateOnly === true,
            onPress: toggle('agreedRateOnly'),
          },
        ]
      : []),
    ...(hasPrice
      ? [
          {
            id: 'price',
            kind: 'sheet' as const,
            label: priceChipLabel(filters, copy, filtersCopy.nightlyPriceLabel),
            active:
              filters.minimumNightlyPrice !== undefined ||
              filters.maximumNightlyPrice !== undefined,
            onPress: openSheet('price', 'price'),
          },
        ]
      : []),
    ...(offers.some(({ distance }) => distance !== undefined)
      ? [
          {
            id: 'distance',
            kind: 'sheet' as const,
            label:
              filters.maximumDistance === undefined
                ? filtersCopy.distanceLabel
                : filtersCopy.distanceUpToLabel(filters.maximumDistance),
            active: filters.maximumDistance !== undefined,
            onPress: openSheet('distance', 'distance'),
          },
        ]
      : []),
    ...(offers.some(({ rating }) => rating !== undefined)
      ? [
          {
            id: 'rating',
            kind: 'sheet' as const,
            label:
              bands.length === 0
                ? filtersCopy.ratingLabel
                : bands.map((floor) => ratingBandLabel(floor, filtersCopy)).join(' · '),
            active: bands.length > 0,
            onPress: openSheet('rating', 'rating'),
          },
        ]
      : []),
    ...amenities.map(({ id, label }) => ({
      id: `amenity:${id}`,
      kind: 'toggle' as const,
      label,
      active: filters.amenityIds.includes(id),
      hiddenWhenInactive: true,
      onPress: () => {
        applyFilters({
          ...filters,
          amenityIds: filters.amenityIds.filter((amenityId) => amenityId !== id),
        });
      },
    })),
  ];
}

type SheetsProps = HotelResultsFilterChipsProps & {
  readonly sheet: OpenSheet;
  readonly onOpenChange: (open: boolean) => void;
  readonly openerRef: RefObject<HTMLElement | null>;
  readonly priceFloor: number | undefined;
  readonly priceCeiling: number | undefined;
  readonly applyFilters: (next: HotelResultsFilterValue) => void;
};

/** As folhas de valor: diária (faixa), distância (uma escolha) e nota
 *  (faixas combináveis) — cada uma com rascunho, Redefinir e Aplicar. */
function HotelValueSheets({
  offers,
  currency,
  filters,
  filtersCopy,
  copy,
  locked,
  countsPartial,
  sheet,
  onOpenChange,
  openerRef,
  priceFloor,
  priceCeiling,
  applyFilters,
}: SheetsProps): ReactNode {
  const countMatching = (
    axis: 'ratingBands' | 'maximumDistance',
    matchesAxis: (facts: HotelFilterableOffer) => boolean,
  ): number =>
    offers.filter((facts) => matchesHotelResultsFilters(facts, filters, axis) && matchesAxis(facts))
      .length;
  /* #3399 (c): as duas folhas são cortes NO SERVIDOR; com a lista parcial a
     contagem local mede a página, não o conjunto — linha sem número (ver
     `countsPartial` em `HotelResultsFilters`). */
  const countServerCut = countsPartial ? () => undefined : countMatching;
  const bands = filters.ratingBands ?? [];
  const shared = {
    onOpenChange,
    returnFocusTo: openerRef,
    backLabel: copy.backLabel,
    resetLabel: filtersCopy.resetFiltersLabel,
    applyLabel: filtersCopy.applyFiltersLabel,
    locked,
  };
  return (
    <>
      {priceFloor !== undefined && priceCeiling !== undefined && (
        <ResultsChipSheet
          {...shared}
          open={sheet === 'price'}
          title={filtersCopy.nightlyPriceLabel}
          value={{ minimum: filters.minimumNightlyPrice, maximum: filters.maximumNightlyPrice }}
          emptyValue={{}}
          onApply={({ minimum, maximum }) => {
            applyFilters({
              ...filters,
              minimumNightlyPrice: minimum,
              maximumNightlyPrice: maximum,
            });
          }}
        >
          {(draft, setDraft) => (
            <PriceRangeSection
              title={filtersCopy.nightlyPriceLabel}
              floor={priceFloor}
              ceiling={priceCeiling}
              currency={currency}
              value={draft}
              minimumLabel={filtersCopy.minimumPriceLabel}
              maximumLabel={filtersCopy.maximumPriceLabel}
              locked={locked}
              onChange={setDraft}
            />
          )}
        </ResultsChipSheet>
      )}
      <ResultsChipSheet<number | undefined>
        {...shared}
        open={sheet === 'distance'}
        title={filtersCopy.distanceLabel}
        value={filters.maximumDistance}
        emptyValue={undefined}
        onApply={(maximumDistance) => {
          applyFilters({ ...filters, maximumDistance });
        }}
      >
        {(draft, setDraft) => (
          <SingleChoiceSection
            title={filtersCopy.distanceLabel}
            options={DISTANCE_THRESHOLDS}
            current={draft}
            locked={locked}
            labelOf={(distance) => filtersCopy.distanceUpToLabel(distance)}
            countOf={(distance) =>
              countServerCut(
                'maximumDistance',
                ({ distance: candidate }) => candidate !== undefined && candidate <= distance,
              )
            }
            onPick={setDraft}
          />
        )}
      </ResultsChipSheet>
      <ResultsChipSheet<readonly HotelRatingBandFloor[]>
        {...shared}
        open={sheet === 'rating'}
        title={filtersCopy.ratingLabel}
        value={bands}
        emptyValue={[]}
        onApply={(next) => {
          applyFilters({ ...filters, ratingBands: next.length === 0 ? undefined : next });
        }}
      >
        {(draft, setDraft) => (
          <div className="-mx-1 flex flex-col">
            {HOTEL_RATING_BAND_FLOORS.map((floor) => {
              const selected = draft.includes(floor);
              const { from, to } = hotelRatingBandBounds(floor);
              const next = selected ? draft.filter((band) => band !== floor) : [...draft, floor];
              return (
                <CheckFilterRow
                  key={floor}
                  label={ratingBandLabel(floor, filtersCopy)}
                  checked={selected}
                  count={countServerCut(
                    'ratingBands',
                    ({ rating }) =>
                      rating !== undefined && rating >= from && (to === undefined || rating < to),
                  )}
                  disabled={locked}
                  onToggle={() => {
                    setDraft(next);
                  }}
                />
              );
            })}
          </div>
        )}
      </ResultsChipSheet>
    </>
  );
}

/**
 * A fila de chips do HOTEL (#3140): os dois fixos, os três liga/desliga
 * (política, café, reembolso), diária, distância e nota com folha parcial, e
 * um chip por comodidade ativa (que o toque desliga). Os toques aplicam na
 * hora — diferente do painel completo, que é rascunho até o Aplicar — porque
 * aqui o corte é UM eixo, à vista, e desfazer é outro toque.
 */
export function HotelResultsFilterChips(props: HotelResultsFilterChipsProps): ReactNode {
  const {
    offers,
    currency,
    filters,
    order,
    filtersCopy,
    sortCopy,
    copy,
    locked,
    countsPartial,
    collapsed = false,
  } = props;
  const [sheet, setSheet] = useState<OpenSheet>(null);
  const chipRefs = useRef(new Map<string, HTMLButtonElement>());
  /** O chip que abriu a folha em cena — o alvo do foco de volta. */
  const openerRef = useRef<HTMLElement | null>(null);
  const { priceFloor, priceCeiling, amenities } = useFilterCatalog(offers);
  const applyFilters = (next: HotelResultsFilterValue) => {
    props.onQueryChange({ filters: next, order });
  };
  const openSheet = (id: string, next: Exclude<OpenSheet, null>) => () => {
    openerRef.current = chipRefs.current.get(id) ?? null;
    setSheet(next);
  };
  const onOpenChange = (open: boolean) => {
    if (!open) setSheet(null);
  };
  const chips = hotelChips({
    offers,
    filters,
    order,
    filtersCopy,
    sortCopy,
    copy,
    hasPrice: priceFloor !== undefined && priceCeiling !== undefined,
    amenities,
    applyFilters,
    openSheet,
  });
  return (
    <>
      <ResultsFilterChips
        chips={chips}
        label={copy.toolbarLabel}
        disabled={locked}
        collapsed={collapsed}
        chipRefs={chipRefs}
      />
      <HotelResultsFiltersSheet
        open={sheet === 'filters'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        offers={offers}
        currency={currency}
        value={filters}
        copy={filtersCopy}
        locked={locked}
        countsPartial={countsPartial}
        onApply={applyFilters}
      />
      <ResultsSortSheet
        open={sheet === 'sort'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        title={sortCopy.sortLabel}
        backLabel={copy.backLabel}
        orders={HOTEL_RESULTS_ORDERS}
        value={order}
        orderLabel={(candidate) => sortCopy.orderLabel(candidate)}
        locked={locked}
        onChange={(next) => {
          props.onQueryChange({ filters, order: next });
        }}
      />
      <HotelValueSheets
        {...props}
        sheet={sheet}
        onOpenChange={onOpenChange}
        openerRef={openerRef}
        priceFloor={priceFloor}
        priceCeiling={priceCeiling}
        applyFilters={applyFilters}
      />
    </>
  );
}
